// ── Collateral Management — Borrowing Base detail (AR insights + manual edit) ──
import { useState, useEffect } from "react";
import { B } from "../../shared/theme";
import { fmt, fmtDate, fmtRatio } from "../../shared/format";
import { Card, ErrorMsg, SectionHeader, Spinner, Td, TdNum, Btn, Icon, FInput, FInputNum, FSelect, Modal, TabBtn, Pagination } from "../../shared/ui";
import { useApp } from "../../shared/appContext";
import { inventoryInsights } from "../../shared/inventoryInsights";
import { StackTrend, BarTrend, ArExcessTrend, TH_WRAP } from "./trendCharts";
import { ArTrendCard, ArRollforwardCard, ArQualityCard, InventoryTab, RowCapScroll } from "./collateralCards";
import { SlowMovingTab } from "./slowMovingTab";
import { ApAgingFindings, useApThresholds } from "./ApAgingFindings";
import { ArPeriodCard, ArRollCard } from "./arTrendCards";

// Invoice-date aging — populated for both invoice- and debtor-grain BBCs (due-date aging is blank on debtor-grain files).
const AGING = [
  { key: "inv_0_30", label: "0–30", color: "#12b5db" },
  { key: "inv_31_60", label: "31–60", color: "#ffb20d" },
  { key: "inv_61_90", label: "61–90", color: "#e07c1a" },
  { key: "inv_91_120", label: "91–120", color: "#c15418" },
  { key: "inv_over120", label: ">120", color: "#993C1D" },
];
// Per-debtor aging columns (byDebtor sums) paired with their table headers.
const DBKT = [
  { key: "inv_0_30", label: "0-30 Days", tot: "b0" },
  { key: "inv_31_60", label: "31-60 Days", tot: "b1" },
  { key: "inv_61_90", label: "61-90 Days", tot: "b2" },
  { key: "inv_91_120", label: "91-120 Days", tot: "b3" },
  { key: "inv_over120", label: ">120 Days", tot: "b4" },
];
// Categorical palette for the Top-10 Debtors / Top-10 Vendors multi-line trends.
const PARTY_COLORS = ["#12b5db", "#ffb20d", "#0F6E56", "#993C1D", "#6a4c93", "#e07c1a", "#116588", "#c15418", "#8a5a00", "#5A8F3C"];
// A/P by-vendor due-date aging buckets (APWaterfallLine dd_* columns).
const APBKT = [
  { key: "dd_current", label: "Current" },
  { key: "dd_1_30", label: "1-30" },
  { key: "dd_31_60", label: "31-60" },
  { key: "dd_61_90", label: "61-90" },
  { key: "dd_over90", label: ">90" },
];

const iso = d => d ? String(d).slice(0, 10) : "";

// A/R Trend chart — nested subsets of one measure → sequential cyan ramp (light → dark).
const TSER = [
  { id: "end", label: "Ending A/R", key: "ar_ending_balance", color: "#A5E3F1", w: 0.72 },
  { id: "elig", label: "Eligible A/R", key: "ar_total_eligible", color: "#12b5db", w: 0.48 },
  { id: "net", label: "Net Avail A/R", key: "ar_net_available", color: "#116588", w: 0.26 },
];
// Slow-moving series, same nested-subset shape as the A/R trend: the widest bar
// is the whole holding and each narrower one sits inside it, so the excess
// buckets read as a portion of the total rather than as separate quantities.
const SMSER = [
  { id: "cost", label: "Inventory at cost", key: "sm_total_cost", color: "#A5E3F1", w: 0.72 },
  { id: "ttm", label: "Cost > TTM", key: "sm_xs_cost_ttm", color: "#12b5db", w: 0.48 },
  { id: "t24m", label: "Cost > 24M", key: "sm_xs_cost_t24m", color: "#116588", w: 0.26 },
];
// A/P Aging — by DUE DATE (Current / 1-30 / 31-60 / 61-90 / >90), mirrors the A/R Aging card.
// ── Inventory ──────────────────────────────────────────────────────────────
// NESTED, NOT PARTITIONED. Gross contains eligible contains available — each
// is the one above it after a deduction — so these are drawn as bars inside
// bars (the `w` fraction), never stacked. Stacking them would draw a total of
// roughly three times the inventory that exists.
const INVSER = [
  { key: "inv_gross",         label: "Gross (at cost)", color: "#A5E3F1", w: 1.00 },
  { key: "inv_total_eligible", label: "Eligible",        color: "#12b5db", w: 0.68 },
  { key: "inv_net_available", label: "Net available",   color: "#116588", w: 0.38 },
];

// The six buckets bbcParser's invNorm() collapses every borrower's own wording
// into. Fixed order and fixed colours so a category keeps its colour from one
// certificate to the next — a mix chart whose colours move is unreadable.
// The four measures a category carries, each a step down the same ladder:
// gross, less ineligible, times the advance rate, is what that category makes
// available. Offered as a switch rather than four charts because the SHAPE is
// the point — a mix that looks balanced at gross and lopsided at available is
// telling you which category the ineligibles and advance rates are eating.
//
// NOTE the last one is the CATEGORY's available, not the header's net
// available. Sublimit and reserve are caps on inventory as a whole and cannot
// be attributed to a category, so they are not in this chart. The table names
// both so the difference is visible rather than a discrepancy someone chases.
const INVMEAS = [
  { key: "gross",      label: "Gross",      header: "inv_gross" },
  { key: "ineligible", label: "Ineligible", header: "inv_total_ineligible" },
  { key: "eligible",   label: "Eligible",   header: "inv_total_eligible" },
  { key: "available",  label: "Available",  header: "inv_available" },
];

const INVCAT = [
  { key: "RM",                 label: "Raw material",  color: "#A5E3F1" },
  { key: "WIP",                label: "WIP",           color: "#7cd4eb" },
  { key: "FG",                 label: "Finished goods", color: "#12b5db" },
  { key: "Packaging/Supplies", label: "Packaging",     color: "#ffb20d" },
  { key: "General",            label: "General",       color: "#e07c1a" },
  { key: "Other",              label: "Other",         color: "#993C1D" },
];

const APAGING = [
  { key: "ap_dd_current", label: "Current", color: "#12b5db" },
  { key: "ap_dd_1_30", label: "1–30", color: "#ffb20d" },
  { key: "ap_dd_31_60", label: "31–60", color: "#e07c1a" },
  { key: "ap_dd_61_90", label: "61–90", color: "#c15418" },
  { key: "ap_dd_over90", label: ">90", color: "#993C1D" },
];
const fmtShort = v => { const a = Math.abs(v); return (a >= 1e6 ? (v / 1e6).toFixed(1) + "M" : a >= 1e3 ? Math.round(v / 1e3) + "K" : String(Math.round(v))); };
const monthLbl = d => { const s = String(d || "").slice(0, 10); const p = s.split("-"); return p.length >= 3 ? p[1] + "/" + p[2] + "/" + p[0].slice(2) : (p.length >= 2 ? p[1] + "/" + p[0].slice(2) : s); };

// Fallback only — the concentration limit is stored per-BBC (BorrowingBase.concentration_limit) and editable.
const DEFAULT_CONCENTRATION_LIMIT = 0.30;

// Concentration limits are STORED as decimals (0.30 = 30%) in both
// BorrowingBase.concentration_limit and DebtorConcentrationLimit.concentration_limit.
// Every place on this page that DISPLAYS one shows a percent — the debtor grid always
// did, and the certificate field now matches, so a limit reads the same way wherever
// you meet it. Storage is unchanged; conversion happens only at the edges of the form.
// The rounding guards the usual float artefact (0.30 * 100 = 30.000000000000004).
const pctFromDecimal = v =>
  (v === null || v === undefined || v === "") ? "" : String(Math.round(parseFloat(v) * 1e6) / 1e4);
const decimalFromPct = v => {
  const n = parseFloat(v);
  return (v === null || v === undefined || v === "" || isNaN(n)) ? null : Math.round(n * 1e4) / 1e6;
};

export const BorrowingBaseDetail = () => {
  const { api, go, view, role, currentUser, setConfirm, setModal, delRec, audit } = useApp();
  const [data, setData] = useState(null);
  const [hist, setHist] = useState([]);
  const apTh = useApThresholds();   // A/P aging "What changed" (shared/apInsights.js)
  const [lines, setLines] = useState(null);   // invoice-level detail (invoice-grain only)
  const [apLines, setApLines] = useState(null);   // invoice-level AP detail (for the A/P-by-vendor drill-down)
  const [detailDebtor, setDetailDebtor] = useState(null);  // debtor whose invoices show in the popup
  const [detailVendor, setDetailVendor] = useState(null);  // vendor whose AP invoices show in the popup
  const [smLines, setSmLines] = useState(null);   // SKU-level slow-moving inventory (only some borrowers report it)
  const [smQ, setSmQ] = useState("");             // SKU / description filter
  const [smIncOnly, setSmIncOnly] = useState(false);// show everything; the Included column carries the borrower's call
  const [smTrendOn, setSmTrendOn] = useState({ cost: true, ttm: true, t24m: true });
  const [smTrendIncOnly, setSmTrendIncOnly] = useState(false);  // trend basis; everything by default
  const [topSkuXsOff, setTopSkuXsOff] = useState({});  // toggled-OFF SKUs in the excess Top-10
  const [topSkuOff, setTopSkuOff] = useState({});  // toggled-OFF SKUs in the Top-10 trend
  const [smPage, setSmPage] = useState(1);         // By SKU pager
  const [tab, setTab] = useState("overview");   // Overview | A/R Detail | A/P Detail | Slow Moving Inventory
  const [partyTrend, setPartyTrend] = useState({ debtors: [], vendors: [], skus: [] });  // per-debtor/vendor/SKU balances across all BBCs
  const [topDebtorOff, setTopDebtorOff] = useState({});   // toggled-OFF debtors in the Top-10 trend (default all on)
  const [debtorDesig, setDebtorDesig] = useState("");   // designation (source tab) filter for the Debtors card
  const [vendorDesig, setVendorDesig] = useState("");   // designation filter for the A/P by Vendor card
  const [topVendorOff, setTopVendorOff] = useState({});
  const [trendOn, setTrendOn] = useState({ end: true, elig: true, net: true, excess: true });
  // Inventory over time. Its own endpoint because nothing joined
  // InventoryCategoryLine across as-of dates before — the rows were written on
  // every certificate and only ever read one certificate at a time.
  const [invTrend, setInvTrend] = useState({ header: [], categories: [] });
  const [invSerOn, setInvSerOn] = useState({ inv_gross: true, inv_total_eligible: true, inv_net_available: true });
  const [invCatOn, setInvCatOn] = useState({});          // toggled-OFF buckets
  const [invInclExcluded, setInvInclExcluded] = useState(true);
  const [invMeas, setInvMeas] = useState("gross");
  const [trendApOn, setTrendApOn] = useState({ ap_dd_current: true, ap_dd_1_30: true, ap_dd_31_60: true, ap_dd_61_90: true, ap_dd_over90: true });
  const [trendArAgeOn, setTrendArAgeOn] = useState({ inv_0_30: true, inv_31_60: true, inv_61_90: true, inv_91_120: true, inv_over120: true });
  const [dsort, setDsort] = useState({ key: "invoicedate", dir: "asc" });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  // edit state
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});          // header field edits
  const [lineEdits, setLineEdits] = useState({}); // { certLineId: value }
  const [invEdits, setInvEdits] = useState({});   // { colnum: { category_label, category_norm, gross, ineligible, eligible, advance_rate, available, excluded } }
  const [newInv, setNewInv] = useState([]);       // rows added during editing (no colnum yet) — [{ tempId, category_label, ... }]
  const [busy, setBusy] = useState(false);
  const [saveErr, setSaveErr] = useState(null);
  // loan link + collateral-entry state
  const [loans, setLoans] = useState([]);
  const [collHeaders, setCollHeaders] = useState([]);       // all collateral headers on this loan (for prior-period carry-forward)
  const [existingColl, setExistingColl] = useState(null);   // collateral header already on this loan for this as-of date
  const [postErr, setPostErr] = useState(null);
  // debtor-level limit editing (account-scoped, carries forward across BBCs)
  const [editLimits, setEditLimits] = useState(false);
  const [limEdits, setLimEdits] = useState({});   // { consolidatedname: percentString }
  const [savingLim, setSavingLim] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("getBorrowingBase", { id: view.bbid })
      .then(d => {
        setData(d); setLoading(false);
        if (d?.header?.accountid) {
          api.get("getBorrowingBases", { accountid: d.header.accountid }).then(h => setHist(Array.isArray(h) ? h : [])).catch(() => {});
          api.get("getLoans?accountid=" + d.header.accountid).then(ls => setLoans(Array.isArray(ls) ? ls : [])).catch(() => setLoans([]));
          // Same enumerate-the-keys discipline as the party trend below: this
          // rebuilds rather than spreads, so anything added server-side has to
          // be named here too.
          api.get("getInventoryTrend?accountid=" + d.header.accountid)
            .then(t => setInvTrend({
              header: Array.isArray(t?.header) ? t.header : [],
              categories: Array.isArray(t?.categories) ? t.categories : [],
            }))
            .catch(() => setInvTrend({ header: [], categories: [] }));
          api.get("getCollateralPartyTrend?accountid=" + d.header.accountid)
            // Every key the endpoint returns has to be listed here — this
            // rebuilds the object rather than spreading it, so a series added
            // server-side is silently dropped until it is named. That is exactly
            // what happened to `skus`.
            .then(p => setPartyTrend({
              debtors: Array.isArray(p?.debtors) ? p.debtors : [],
              vendors: Array.isArray(p?.vendors) ? p.vendors : [],
              skus: Array.isArray(p?.skus) ? p.skus : [],
            }))
            .catch(() => setPartyTrend({ debtors: [], vendors: [], skus: [] }));
        }
        // Does a collateral entry already exist on this loan for this as-of date? (posted from here or entered manually)
        if (d?.header?.loanid) {
          const bd = iso(d.header.asofdate);
          api.get("getCollateralHeaders?loanid=" + d.header.loanid)
            .then(list => { const arr = Array.isArray(list) ? list : []; setCollHeaders(arr); setExistingColl(arr.find(e => iso(e.asofdate) === bd) || null); })
            .catch(() => { setCollHeaders([]); setExistingColl(null); });
        } else { setCollHeaders([]); setExistingColl(null); }
        if (d?.aging?.grain === "invoice") api.get("getBorrowingBaseLines", { id: view.bbid }).then(x => setLines(Array.isArray(x) ? x : [])).catch(() => setLines([]));
        else setLines(null);
        // AP invoice detail (for the A/P-by-vendor drill-down) — only when the certificate has an AP Waterfall.
        if (d?.header?.ap_open_balance != null && parseFloat(d.header.ap_open_balance) !== 0)
          api.get("getBorrowingBaseAPLines", { id: view.bbid }).then(x => setApLines(Array.isArray(x) ? x : [])).catch(() => setApLines([]));
        else setApLines(null);
        // Slow-moving inventory — most certificates have none, so this is gated
        // on the header roll-up rather than fetched speculatively for every BBC.
        if (d?.header?.sm_sku_count != null && parseInt(d.header.sm_sku_count, 10) > 0)
          api.get("getBorrowingBaseSlowMoving", { id: view.bbid }).then(x => setSmLines(Array.isArray(x) ? x : [])).catch(() => setSmLines([]));
        else setSmLines(null);
      })
      .catch(e => { setErr(e.message); setLoading(false); });
  };
  useEffect(() => { load(); setEditing(false); }, [view.bbid]);
  // Searching or toggling shrinks the SKU list; without this you can be left
  // on page 6 of a 2-page result looking at an empty table. Declared here
  // rather than beside the pager below because the early returns underneath
  // would make it a conditionally-called hook.
  useEffect(() => { setSmPage(1); }, [smQ, smIncOnly]);

  if (loading) return <Spinner />;
  if (err) return <ErrorMsg msg={err} onRetry={load} />;
  if (!data) return null;

  const h = data.header, ag = data.aging || {}, debtors = data.byDebtor || [];
  // Designation (source tab, e.g. US / CAN) — column + filter when the AR came from more than one tab.
  const debtorDesigs = [...new Set(debtors.map(d => d.designation).filter(Boolean))].sort();
  const shownDebtors = debtorDesig ? debtors.filter(d => (d.designation || "") === debtorDesig) : debtors;
  const agingTotal = AGING.reduce((s, b) => s + (parseFloat(ag[b.key]) || 0), 0);
  // ── A/P (present only when the certificate has an AP Waterfall) ──
  const hasAP = h.ap_open_balance != null && parseFloat(h.ap_open_balance) !== 0;
  const apAgingTotal = APAGING.reduce((s, b) => s + (parseFloat(h[b.key]) || 0), 0);

  // ── Slow-moving inventory ──
  // Driven by the header roll-up, not by smLines, so the tab is decided before
  // the fetch returns and does not appear and then vanish mid-render.
  const hasSlowMoving = h.sm_sku_count != null && parseInt(h.sm_sku_count, 10) > 0;
  const smAll = smLines || [];
  // "Included" is the borrower's own call, normalized at parse time from either
  // an Include? column or an inverted Exclude column. Rows where they said
  // nothing (null) are kept — silence is not exclusion.
  const smRows = (smIncOnly ? smAll.filter(l => l.include_flag !== false) : smAll)
    .filter(l => {
      const q = smQ.trim().toLowerCase();
      if (!q) return true;
      return String(l.unique_id || "").toLowerCase().includes(q)
          || String(l.description || "").toLowerCase().includes(q);
    });
  const smSum = k => smRows.reduce((t, l) => t + (parseFloat(l[k]) || 0), 0);
  // Paged rather than capped. The table used to render the first 500 and simply
  // stop: the remainder counted toward the totals but could not be reached, so a
  // SKU sorted below 500th was unviewable.
  //
  // Derived, not a hook — usePagination holds state, and this sits below the
  // loading/error early returns where a hook cannot go. The page number itself
  // is useState at the top of the component. Clamped so a stale page number can
  // never render an empty table.
  const SM_PAGE = 100;
  const smTotalPages = Math.max(1, Math.ceil(smRows.length / SM_PAGE));
  const smPageNow = Math.min(smPage, smTotalPages);
  const smPaged = smRows.slice((smPageNow - 1) * SM_PAGE, smPageNow * SM_PAGE);
  // Whether the borrower reported these at all. A column nobody supplied is
  // hidden rather than shown as a wall of dashes that reads like a broken parse.
  const smHas = k => smAll.some(l => l[k] !== null && l[k] !== undefined);
  const smHasT24M = smHas("xs_cost_t24m") || smHas("t24m_units_sold");
  const smHasProdDate = smHas("prod_start_date");
  // ── inventory ──
  const inv = data.inventory || [];
  const INV_ORDER = ["RM", "WIP", "FG", "General", "Packaging/Supplies", "Other"];
  const invSorted = inv.slice().sort((a, b) => { const d = INV_ORDER.indexOf(a.category_norm) - INV_ORDER.indexOf(b.category_norm); return d !== 0 ? d : ((a.colnum || 0) - (b.colnum || 0)); });
  const invTot = inv.filter(c => !c.excluded).reduce((a, c) => ({ g: a.g + (parseFloat(c.gross) || 0), i: a.i + (parseFloat(c.ineligible) || 0), e: a.e + (parseFloat(c.eligible) || 0), av: a.av + (parseFloat(c.available) || 0) }), { g: 0, i: 0, e: 0, av: 0 });
  // Group categories by normalized bucket; carry subtotals (shown only when a bucket has >1 category).
  const invByBucket = [];
  let _ib = null;
  invSorted.forEach(c => {
    if (!_ib || _ib.norm !== c.category_norm) { _ib = { norm: c.category_norm, rows: [], g: 0, i: 0, e: 0, av: 0 }; invByBucket.push(_ib); }
    _ib.rows.push(c);
    if (!c.excluded) { _ib.g += parseFloat(c.gross) || 0; _ib.i += parseFloat(c.ineligible) || 0; _ib.e += parseFloat(c.eligible) || 0; _ib.av += parseFloat(c.available) || 0; }
  });
  // Once any bucket splits into multiple categories, show a subtotal for every (non-excluded) bucket for consistency.
  const invHasSub = invByBucket.some(g => g.rows.length > 1);
  const hasInv = (h.inv_gross != null && parseFloat(h.inv_gross) !== 0) || inv.length > 0;

  // ── inventory over time ──────────────────────────────────────────────────
  // Dates come from the HEADER series, not the categories: a certificate that
  // reported a total but no category breakdown still belongs on the timeline,
  // and dropping it would draw a gap where there is data.
  const invHist = invTrend.header;
  const invDates = invHist.map(r => String(r.asofdate || "").slice(0, 10));
  const invCatRows = invTrend.categories.filter(c => invInclExcluded || !c.excluded);
  // Which buckets this borrower actually reports, in the fixed order. A chart
  // with a legend entry for every bucket in the vocabulary — most of them flat
  // at zero — hides the two that move.
  const invCatsPresent = INVCAT.filter(b =>
    invCatRows.some(c => c.category_norm === b.key && (parseFloat(c.gross) || 0) !== 0));
  // Measure is a parameter, not a second function. Four near-identical readers
  // is four places for one of them to keep pointing at gross after the others
  // moved on.
  const invCatAt = (bucket, bbid, meas) => {
    const r = invCatRows.find(c => c.category_norm === bucket && c.bbid === bbid);
    return r ? (parseFloat(r[meas || invMeas]) || 0) : 0;
  };
  const invMeasDef = INVMEAS.find(m => m.key === invMeas) || INVMEAS[0];
  const invCatShown = invCatsPresent.filter(b => !invCatOn[b.key]);
  const invPeriods = invHist.length;
  // Recomputed from the trend payload rather than stored. It is cheap, and a
  // cached finding that outlives the data it described is worse than none.
  const invFindings = inventoryInsights(invTrend);
  // Full certificate reproduction — group all cert lines by section (in sheet order).
  const certGrouped = [];
  let _cs = null;
  (data.certLines || []).forEach(l => { if (l.section !== _cs) { _cs = l.section; certGrouped.push({ header: l.section }); } certGrouped.push({ line: l }); });
  const dtot = shownDebtors.reduce((a, d) => ({
    open: a.open + (parseFloat(d.open_balance) || 0),
    elig: a.elig + (parseFloat(d.eligible) || 0),
    past: a.past + (parseFloat(d.past_due) || 0),
    b0: a.b0 + (parseFloat(d.inv_0_30) || 0),
    b1: a.b1 + (parseFloat(d.inv_31_60) || 0),
    b2: a.b2 + (parseFloat(d.inv_61_90) || 0),
    b3: a.b3 + (parseFloat(d.inv_91_120) || 0),
    b4: a.b4 + (parseFloat(d.inv_over120) || 0),
  }), { open: 0, elig: 0, past: 0, b0: 0, b1: 0, b2: 0, b3: 0, b4: 0 });
  const dtotPct = h.ar_ending_balance ? dtot.open / parseFloat(h.ar_ending_balance) : 0;
  const climit = (h.concentration_limit != null && h.concentration_limit !== "") ? parseFloat(h.concentration_limit) : DEFAULT_CONCENTRATION_LIMIT;
  // Per-account debtor-level overrides (carry forward across BBCs); fall back to the BBC default.
  const limMap = {};
  (data.debtorLimits || []).forEach(l => { if (l.consolidatedname != null) limMap[l.consolidatedname] = parseFloat(l.concentration_limit); });
  const effLimit = name => (limMap[name] != null && !isNaN(limMap[name])) ? limMap[name] : climit;
  // % of total eligible over that debtor's limit, in dollars of the debtor's own eligible.
  const concIneligFor = (name, elig) => { const L = effLimit(name); return (dtot.elig && (elig / dtot.elig) > L) ? ((elig / dtot.elig) - L) * elig : 0; };
  const dtotConcInelig = shownDebtors.reduce((s, d) => s + concIneligFor(d.consolidatedname, parseFloat(d.eligible) || 0), 0);
  const boxHref = h.box_url || (h.box_file_id ? `https://app.box.com/file/${h.box_file_id}` : null);

  // ── A/P by vendor (open A/P by due-date aging) ──
  const byVendorAP = data.byVendorAP || [];
  const vendorDesigs = [...new Set(byVendorAP.map(d => d.designation).filter(Boolean))].sort();
  const shownVendors = vendorDesig ? byVendorAP.filter(d => (d.designation || "") === vendorDesig) : byVendorAP;
  const apVendTot = shownVendors.reduce((a, d) => { a.open += parseFloat(d.open_balance) || 0; APBKT.forEach(b => a[b.key] += parseFloat(d[b.key]) || 0); return a; }, { open: 0, dd_current: 0, dd_1_30: 0, dd_31_60: 0, dd_61_90: 0, dd_over90: 0 });
  const exportVendorsAP = () => {
    const esc = v => '"' + String(v == null ? "" : v).replace(/"/g, '""') + '"';
    const cols = ["Vendor", ...(vendorDesigs.length ? ["Designation"] : []), "Open Balance", "% of A/P", ...APBKT.map(b => b.label)];
    const rows = shownVendors.map(d => { const ob = parseFloat(d.open_balance) || 0; const p = apVendTot.open ? (ob / apVendTot.open * 100) : 0;
      return [d.consolidatedname, ...(vendorDesigs.length ? [d.designation || ""] : []), ob, p.toFixed(1), ...APBKT.map(b => parseFloat(d[b.key]) || 0)].map(esc).join(","); });
    const url = URL.createObjectURL(new Blob([[cols.map(esc).join(","), ...rows].join("\r\n")], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a"); a.href = url; a.download = `AP_Vendors_${h.accountid || "acct"}_${iso(h.asofdate)}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };
  const apVendorRows = detailVendor != null ? (apLines || []).filter(l => l.consolidatedname === detailVendor) : [];
  const hasDesigAP = apVendorRows.some(r => r.designation);
  const apVendorTot = apVendorRows.reduce((a, r) => { a.ob += parseFloat(r.openbalance) || 0; APBKT.forEach(b => a[b.key] += parseFloat(r[b.key]) || 0); return a; }, { ob: 0, dd_current: 0, dd_1_30: 0, dd_31_60: 0, dd_61_90: 0, dd_over90: 0 });

  // ── Top-10 party trends (top 10 chosen from the most recent BBC, then trended across history) ──
  // nameKey/valKey default to the debtor/vendor shape, so the two existing calls
  // are unchanged; slow-moving passes unique_id and whichever measure is selected.
  // altKey carries a SECOND measure through untouched — ranking and plotting
  // still use valKey, but the card can show the other figure beside it. Used to
  // put total cost next to a SKU ranked on units, where the count alone does not
  // say whether it matters.
  const buildParty = (rows, nameKey = "consolidatedname", valKey = "open_balance", altKey = null) => {
    const byDate = {}; const altByDate = {}; const dset = new Set();
    (rows || []).forEach(r => { const d = iso(r.asofdate); if (!d) return; dset.add(d); (byDate[d] = byDate[d] || {}); byDate[d][r[nameKey]] = (byDate[d][r[nameKey]] || 0) + (parseFloat(r[valKey]) || 0);
      if (altKey) { (altByDate[d] = altByDate[d] || {}); altByDate[d][r[nameKey]] = (altByDate[d][r[nameKey]] || 0) + (parseFloat(r[altKey]) || 0); } });
    const dates = [...dset].sort();                    // ascending (oldest → newest)
    const latest = dates[dates.length - 1];
    const top = latest ? Object.entries(byDate[latest]).sort((a, b) => b[1] - a[1]).slice(0, 10).map(([n]) => n) : [];
    return { dates, byDate, top, altByDate };
  };
  const debtorTrend = buildParty(partyTrend.debtors);
  const vendorTrend = buildParty(partyTrend.vendors);
  // Top-10 SKUs on the selected measure. Rebuilt when the toggle changes, which
  // can reorder the top ten — a SKU can be large in total cost and barely
  // present in excess, and that difference is the point of having both.
  // The trend's basis is chosen here, not in SQL. The endpoint returns every
  // SKU with its include flag, so switching between "everything the borrower
  // reported" and "only what their own totals count" is instant and needs no
  // refetch.
  const smTrendSkus = (partyTrend.skus || [])
    .filter(r => !smTrendIncOnly || r.include_flag !== 0);
  // ── Top-10 SKU series ──
  // Always on the INCLUDED basis, regardless of the trend toggle above. Ranking
  // SKUs the borrower has told us not to count would put items at the top of a
  // watch-list that nobody intends to act on. The cards say so on their face.
  const smTopSkus = (partyTrend.skus || []).filter(r => r.include_flag !== 0);
  // Ranked on units on hand, with cost carried alongside: a count on its own
  // does not say whether the pile matters, and a cheap part in huge quantity is
  // a different conversation from an expensive one in small.
  const skuTrendUnits = buildParty(smTopSkus, "unique_id", "unit_count", "total_cost");
  // Ranked on excess over TTM — the overstock itself. Watching this list turn
  // over, or fail to, is how you tell whether they are actually working it down.
  const skuTrendXs = buildParty(smTopSkus, "unique_id", "xs_cost_ttm");
  const smUnits = v => Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
  const smIncludedNote = "Ranked across the SKUs the borrower marks as included — the same basis as their own totals. Excluded SKUs never appear here, whatever the trend toggle above is set to.";
  // Period-over-period roll-ups, oldest first.
  //
  // Aggregated from the per-SKU rows rather than read from the stored header
  // totals. The header roll-ups are fixed at "included only" — the basis the
  // borrower's own totals use — and cannot answer the other question. Summing
  // the detail lets the same toggle drive the cards, the chart and the table,
  // so all three always describe the same set of rows.
  //
  // Only certificates that actually carried a slow-moving sheet appear: a
  // borrower who reported it twice and then stopped shows two points and a gap,
  // not three with a fabricated zero.
  const smHistory = (() => {
    const by = {};
    for (const r of smTrendSkus) {
      const d = iso(r.asofdate); if (!d) continue;
      const e = by[r.bbid] = by[r.bbid] || {
        id: r.bbid, asofdate: r.asofdate, _d: d,
        sm_sku_count: 0, sm_total_cost: 0, sm_xs_cost_ttm: 0, sm_xs_cost_t24m: 0,
      };
      e.sm_sku_count += 1;
      e.sm_total_cost += parseFloat(r.total_cost) || 0;
      e.sm_xs_cost_ttm += parseFloat(r.xs_cost_ttm) || 0;
      e.sm_xs_cost_t24m += parseFloat(r.xs_cost_t24m) || 0;
    }
    return Object.values(by).sort((a, b) => a._d.localeCompare(b._d));
  })();

  // Renders one Top-10 party trend card (multi-line chart + table); returns null when there's nothing to trend.
  // opts (all optional, so the Debtors and Vendors calls are unchanged):
  //   fmtVal   how to render a value — defaults to money; unit counts pass a
  //            plain number formatter, because "$3,830" for 3,830 pieces of
  //            stock is simply wrong.
  //   note     a line under the title, for saying what the card is filtered to.
  //   altLabel/altOf  a second figure shown on each tile beside the ranked one.
  const partyTrendCard = (title, td, offState, setOffState, grandLabel, opts = {}) => {
    const fmtVal = opts.fmtVal || fmt;
    // Debtors and Vendors need two periods — a one-point trend line says
    // nothing they do not already show elsewhere on the page. The SKU cards pass
    // minPeriods: 1, because the RANKING is useful from the first certificate
    // even when there is nothing to trend yet; the delta badge is simply omitted
    // until there is a prior period to compare with.
    if (!td || td.dates.length < (opts.minPeriods || 2) || td.top.length === 0) return null;
    const asc = td.dates;   // ascending
    const val = (d, n) => td.byDate[d]?.[n] || 0;
    const grandOf = d => Object.values(td.byDate[d] || {}).reduce((s, v) => s + (parseFloat(v) || 0), 0);
    const gmax = Math.max(1, ...asc.flatMap(d => td.top.map(nm => val(d, nm))));
    return (
      <Card style={{ overflowX: "auto", opacity: editing ? 0.6 : 1 }}>
        <div style={{ ...cardTitle, padding: "12px 12px 8px", marginBottom: 0 }}>{title} <span style={{ fontWeight: 400, color: "var(--color-text-secondary)" }}>(top {td.top.length} as of {fmtDate(asc[asc.length - 1])} · {asc.length} periods · Δ vs prior BBC)</span></div>
        {opts.note && <div style={{ padding: "0 12px 8px", fontSize: 12, color: "var(--color-text-secondary)" }}>{opts.note}</div>}
        <div style={{ padding: "10px 12px 6px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 12 }}>
            {td.top.map(nm => {
              const vals = asc.map(d => val(d, nm));
              const last = vals[vals.length - 1];
              // No prior period means no change to report. Without this the
              // badge renders "NaN%" on a first certificate.
              const hasPrev = vals.length > 1;
              const prev = hasPrev ? vals[vals.length - 2] : null;
              const dlt = hasPrev ? last - prev : 0, pct = (hasPrev && prev) ? (dlt / prev * 100) : 0, flat = Math.abs(pct) < 1;
              const col = flat ? "var(--color-text-secondary)" : (dlt > 0 ? "#993C1D" : "#0F6E56");
              const bg = flat ? "#eef1f4" : (dlt > 0 ? "#FBECE7" : "#E1F5EE");
              const SW = 168, SH = 46, sp = 5;
              const sx = i => sp + (SW - 2 * sp) * (vals.length > 1 ? i / (vals.length - 1) : 0.5);
              const sy = v => SH - sp - (SH - 2 * sp) * (v / gmax);
              const pts = vals.map((v, i) => sx(i) + "," + sy(v)).join(" ");
              return (
                <div key={nm} style={{ border: "0.5px solid var(--color-border-tertiary)", borderRadius: 8, padding: "9px 11px" }}>
                  <div style={{ fontSize: 12, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={nm}>{nm}</div>
                  <svg width="100%" viewBox={`0 0 ${SW} ${SH}`} style={{ display: "block", margin: "4px 0" }}>
                    <polygon points={`${sp},${SH - sp} ${pts} ${SW - sp},${SH - sp}`} fill={B.cyan} opacity="0.08" />
                    <polyline points={pts} fill="none" stroke={B.cyan} strokeWidth="1.6" />
                    <circle cx={sx(vals.length - 1)} cy={sy(last)} r="2.8" fill={flat ? B.cyanDark : col} />
                  </svg>
                  <div style={{ fontSize: 15, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{fmtVal(last)}</div>
                  {opts.altOf && <div style={{ fontSize: 11, color: "var(--color-text-secondary)", fontVariantNumeric: "tabular-nums" }}>
                    {opts.altLabel} {fmt(opts.altOf(asc[asc.length - 1], nm))}
                  </div>}
                  {hasPrev && <span style={{ fontSize: 11, fontWeight: 600, padding: "1px 7px", borderRadius: 10, display: "inline-block", marginTop: 3, color: col, background: bg }}>
                    {flat ? "–" : (dlt > 0 ? "▲" : "▼")} {dlt >= 0 ? "+" : "−"}{fmtShort(Math.abs(dlt))} · {dlt >= 0 ? "+" : "−"}{Math.abs(pct).toFixed(0)}%
                  </span>}
                </div>
              );
            })}
          </div>
        </div>
        <div style={{ maxHeight: 240, overflowY: "auto", overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, whiteSpace: "nowrap" }}>
            <thead><tr>
              <th style={{ ...thSticky, textAlign: "left" }}>As-of</th>
              {td.top.map((nm, i) => <th key={nm} style={{ ...thSticky, textAlign: "right" }}><span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: PARTY_COLORS[i % PARTY_COLORS.length], display: "inline-block" }} />{nm}</span></th>)}
              <th style={{ ...thSticky, textAlign: "right" }}>Top {td.top.length} Total</th>
              <th style={{ ...thSticky, textAlign: "right" }}>{grandLabel}</th>
            </tr></thead>
            <tbody>
              {asc.slice().reverse().map(d => {
                const tot = td.top.reduce((s, nm) => s + val(d, nm), 0);
                const grand = grandOf(d);
                const pctSpan = v => <span style={{ fontSize: 10, color: "var(--color-text-secondary)" }}> ({grand ? (v / grand * 100).toFixed(1) : "0.0"}%)</span>;
                return (
                  <tr key={d} style={{ background: iso(h.asofdate) === d ? B.cyanLight : "" }}>
                    <Td style={{ fontWeight: iso(h.asofdate) === d ? 600 : 400 }}>{fmtDate(d)}</Td>
                    {td.top.map(nm => { const v = val(d, nm); return <TdNum key={nm}>{fmtVal(v)}{pctSpan(v)}</TdNum>; })}
                    <TdNum style={{ fontWeight: 600 }}>{fmtVal(tot)}{pctSpan(tot)}</TdNum>
                    <TdNum style={{ fontWeight: 600 }}>{fmtVal(grand)}</TdNum>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    );
  };

  // ── Loan link + Post to Portfolio ──
  const ACTIVE_LOAN = s => !["Terminated", "Paid Off", "Funding in Progress"].includes(s);
  const REVOLVING = ft => /^revolving line of credit$/i.test((ft || "").trim());
  // Borrowing bases apply to revolving lines only; always keep the currently-assigned loan selectable.
  const activeLoans = loans.filter(l => (ACTIVE_LOAN(l.loanstatus) && REVOLVING(l.facilitytype)) || String(l.id) === String(h.loanid));
  const selectedLoan = loans.find(l => String(l.id) === String(h.loanid));
  const loanLabel = selectedLoan?.loannumber || (h.loanid ? String(h.loanid) : "");
  // BBC advance rates are stored as decimals (≤1); the collateral line wants a percentage (85, 40.06).
  const pct = v => { if (v == null || v === "") return ""; const n = parseFloat(v); if (isNaN(n)) return ""; return Math.abs(n) <= 1 ? n * 100 : n; };
  const invBreakout = () => {
    const bk = { invrawmaterials: 0, invworkinprogress: 0, invfinishedgoods: 0, invgeneral: 0, invother: 0 };
    inv.forEach(c => {
      if (c.excluded) return;
      const g = parseFloat(c.gross) || 0;
      if (c.category_norm === "RM") bk.invrawmaterials += g;
      else if (c.category_norm === "WIP") bk.invworkinprogress += g;
      else if (c.category_norm === "FG") bk.invfinishedgoods += g;
      else if (c.category_norm === "General") bk.invgeneral += g;
      else bk.invother += g;   // Packaging/Supplies, Other, or unmapped
    });
    return bk;
  };

  // Build the collateral-entry payload mapped from this certificate (for the user to review, then save).
  const buildPrefill = () => {
    const lines = [{
      collateraltype: "Accounts Receivable",
      grossamount: h.ar_ending_balance ?? "", ineligibleamount: h.ar_total_ineligible ?? "",
      advancerate: pct(h.ar_advance_rate),
      araging0to30: ag.inv_0_30 ?? "", araging31to60: ag.inv_31_60 ?? "",
      araging61to90: ag.inv_61_90 ?? "", araging90plus: ag.inv_91_120 ?? "", araging120plus: ag.inv_over120 ?? "",
    }];
    if (hasInv) lines.push({
      collateraltype: "Inventory",
      grossamount: h.inv_gross ?? "", ineligibleamount: h.inv_total_ineligible ?? "",
      advancerate: pct(h.inv_advance_rate), ...invBreakout(),
    });
    // Carry forward header reserves/sublimits from the most recent collateral entry dated BEFORE this BBC,
    // so the analyst doesn't retype values that persist period-to-period. Only non-zero values carry.
    const bd = iso(h.asofdate);
    const priorList = collHeaders.filter(e => iso(e.asofdate) < bd);
    const prior = priorList.length ? priorList.reduce((a, b) => iso(a.asofdate) > iso(b.asofdate) ? a : b) : null;
    const carry = (field, fallback) => {
      const pv = prior ? parseFloat(prior[field]) : NaN;
      return (isFinite(pv) && pv !== 0) ? prior[field] : fallback;
    };
    // A/P aging (due date) from the certificate → collateral 0-30 / 31-60 / 61-90 / 90+.
    // The BBC splits the freshest bucket into Current + 1-30; combine them into 0-30 so the total ties.
    const num = v => parseFloat(v) || 0;
    const apMap = hasAP ? {
      apaging0to30: num(h.ap_dd_current) + num(h.ap_dd_1_30),
      apaging31to60: h.ap_dd_31_60 ?? "",
      apaging61to90: h.ap_dd_61_90 ?? "",
      apaging90plus: h.ap_dd_over90 ?? "",
    } : {};
    return {
      header: {
        asofdate: bd,
        grossavailabilityreserves: carry("grossavailabilityreserves", ""),
        overadvanceallowance: carry("overadvanceallowance", ""),
        aggregateinventorysublimit: carry("aggregateinventorysublimit", h.inv_sublimit ?? ""),
        aggregateinventoryreserve: carry("aggregateinventoryreserve", h.inv_reserve ?? ""),
        ...apMap,
      },
      lines,
    };
  };

  // Opens the collateral-item form: the existing entry if one exists, else a pre-filled draft
  // the analyst reviews and saves themselves — nothing is written until they click Save Entry.
  const openCollateralForm = () => {
    setPostErr(null);
    if (!h.loanid) { setPostErr("Assign a loan to this certificate first (use Edit)."); return; }
    if (existingColl) { setModal({ type: "collateral-entry", headerId: existingColl.id, loanid: h.loanid, onDone: load }); return; }
    setModal({ type: "collateral-entry", loanid: h.loanid, prefill: buildPrefill(), onDone: load });
  };

  const startEdit = () => {
    setForm({
      ar_ending_balance: h.ar_ending_balance ?? "",
      ar_total_ineligible: h.ar_total_ineligible ?? "",
      ar_total_eligible: h.ar_total_eligible ?? "",
      ar_advance_rate: h.ar_advance_rate ?? "",
      ar_net_available: h.ar_net_available ?? "",
      gross_collateral_avail: h.gross_collateral_avail ?? "",
      borrowing_base_avail: h.borrowing_base_avail ?? "",
      current_loan_balance: h.current_loan_balance ?? "",
      excess_availability: h.excess_availability ?? "",
      concentration_limit: pctFromDecimal(h.concentration_limit),
      inv_gross: h.inv_gross ?? "",
      inv_total_ineligible: h.inv_total_ineligible ?? "",
      inv_total_eligible: h.inv_total_eligible ?? "",
      inv_advance_rate: h.inv_advance_rate ?? "",
      inv_net_available: h.inv_net_available ?? "",
      asofdate: iso(h.asofdate),
      previousasofdate: iso(h.previousasofdate),
      loanid: h.loanid ?? "",
    });
    const le = {};
    (data.certLines || []).forEach(l => { le[l.id] = l.value ?? ""; });
    setLineEdits(le);
    const ie = {};
    (data.inventory || []).forEach(c => {
      ie[c.colnum] = {
        category_label: c.category_label ?? "",
        category_norm: c.category_norm ?? "",
        gross: c.gross ?? "",
        ineligible: c.ineligible ?? "",
        eligible: c.eligible ?? "",
        advance_rate: c.advance_rate ?? "",
        available: c.available ?? "",
        excluded: !!c.excluded,
      };
    });
    setInvEdits(ie);
    setNewInv([]);
    setSaveErr(null);
    setEditing(true);
  };

  const save = async () => {
    setBusy(true); setSaveErr(null);
    try {
      const lines = Object.entries(lineEdits).map(([id, value]) => ({ id: Number(id), value }));
      const inventory = Object.entries(invEdits).map(([colnum, e]) => ({
        colnum: Number(colnum),
        category_label: e.category_label,
        category_norm: e.category_norm,
        gross: e.gross,
        ineligible: e.ineligible,
        eligible: e.eligible,
        advance_rate: e.advance_rate,
        available: e.available,
        excluded: e.excluded ? 1 : 0,
      }));
      const newInventory = newInv
        .filter(r => (r.category_label && String(r.category_label).trim()) || r.gross !== "" || r.available !== "")
        .map(r => ({
          category_label: r.category_label,
          category_norm: r.category_norm,
          gross: r.gross,
          ineligible: r.ineligible,
          eligible: r.eligible,
          advance_rate: r.advance_rate,
          available: r.available,
          excluded: r.excluded ? 1 : 0,
        }));
      // The form holds a percent; the column holds a decimal.
      const header = { ...form, concentration_limit: decimalFromPct(form.concentration_limit) };
      await api.post("updateBorrowingBase", { id: h.id, header, lines, inventory, newInventory, updatedby: currentUser?.email || null });
      if (audit) try { await audit("Updated", "BorrowingBase", h.id, "Manual BBC correction"); } catch (e) {}
      setEditing(false);
      load();
    } catch (e) { setSaveErr(e.message || "Save failed"); }
    setBusy(false);
  };

  const addInvRow = () => setNewInv(rows => [...rows, {
    tempId: "n" + Date.now() + "-" + rows.length,
    category_label: "", category_norm: "Other",
    gross: "", ineligible: "", eligible: "", advance_rate: "", available: "", excluded: false,
  }]);
  const setNewIe = (tempId, field, v) => setNewInv(rows => rows.map(r => r.tempId === tempId ? { ...r, [field]: v } : r));
  const removeNewRow = (tempId) => setNewInv(rows => rows.filter(r => r.tempId !== tempId));

  const remove = () => setConfirm({
    msg: "Delete this borrowing base and all of its line detail? This cannot be undone.",
    onConfirm: () => delRec("BorrowingBase", h.id, "Deleted borrowing base", () => go("collateral-home")),
  });

  const setF = (field, v) => setForm(f => ({ ...f, [field]: v }));

  const startEditLimits = () => {
    const e = {};
    debtors.forEach(d => { const v = limMap[d.consolidatedname]; e[d.consolidatedname] = (v != null && !isNaN(v)) ? (v * 100).toString() : ""; });
    setLimEdits(e); setSaveErr(null); setEditLimits(true);
  };
  const saveLimits = async () => {
    setSavingLim(true); setSaveErr(null);
    try {
      const limits = debtors.map(d => {
        const raw = (limEdits[d.consolidatedname] ?? "").toString().trim();
        const pct = raw === "" ? null : parseFloat(raw);
        return { consolidatedname: d.consolidatedname, concentration_limit: (pct == null || isNaN(pct)) ? null : pct / 100 };
      });
      await api.post("saveDebtorConcentrationLimits", { accountid: h.accountid, updatedby: currentUser?.email || null, limits });
      setEditLimits(false);
      load();
    } catch (e) { setSaveErr(e.message || "Save failed"); }
    setSavingLim(false);
  };

  // ── per-debtor invoice detail (popup): sortable + CSV export ──
  const invoiceGrain = data.aging?.grain === "invoice" && Array.isArray(lines);
  const DCOLS = [
    { k: "invoicenumber", label: "Invoice #", t: "txt" },
    { k: "invoicedate", label: "Invoice Date", t: "date" },
    { k: "duedate", label: "Due Date", t: "date" },
    { k: "openbalance", label: "Open Balance", t: "num" },
    { k: "aging_inv_0_30", label: "0-30", t: "num" },
    { k: "aging_inv_31_60", label: "31-60", t: "num" },
    { k: "aging_inv_61_90", label: "61-90", t: "num" },
    { k: "aging_inv_91_120", label: "91-120", t: "num" },
    { k: "aging_inv_over120", label: ">120", t: "num" },
    { k: "pastdue", label: "Past Due", t: "num" },
    { k: "eligiblear", label: "Eligible", t: "num" },
  ];
  const detailRows = detailDebtor != null ? (lines || []).filter(l => l.consolidatedname === detailDebtor) : [];
  const hasDesigAR = detailRows.some(r => r.designation);
  const sortedDetail = detailRows.slice().sort((a, b) => {
    const c = DCOLS.find(x => x.k === dsort.key) || {};
    if (c.t === "num") { const av = parseFloat(a[dsort.key]) || 0, bv = parseFloat(b[dsort.key]) || 0; return dsort.dir === "asc" ? av - bv : bv - av; }
    const av = a[dsort.key] == null ? "" : String(a[dsort.key]), bv = b[dsort.key] == null ? "" : String(b[dsort.key]);
    return dsort.dir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
  });
  const dTot = detailRows.reduce((a, r) => ({
    ob: a.ob + (parseFloat(r.openbalance) || 0),
    b0: a.b0 + (parseFloat(r.aging_inv_0_30) || 0), b1: a.b1 + (parseFloat(r.aging_inv_31_60) || 0),
    b2: a.b2 + (parseFloat(r.aging_inv_61_90) || 0), b3: a.b3 + (parseFloat(r.aging_inv_91_120) || 0),
    b4: a.b4 + (parseFloat(r.aging_inv_over120) || 0),
    past: a.past + (parseFloat(r.pastdue) || 0), elig: a.elig + (parseFloat(r.eligiblear) || 0),
  }), { ob: 0, b0: 0, b1: 0, b2: 0, b3: 0, b4: 0, past: 0, elig: 0 });
  const tfootCell = { position: "sticky", bottom: 0, background: "#FDF0D5", fontWeight: 700, color: "#603700", borderTop: "1.5px solid #E8C36A", boxShadow: "0 -1px 0 #E8C36A" };
  const dtoggle = k => setDsort(s => s.key === k ? { key: k, dir: s.dir === "asc" ? "desc" : "asc" } : { key: k, dir: "asc" });
  const exportCsv = () => {
    const esc = v => '"' + String(v == null ? "" : v).replace(/"/g, '""') + '"';
    const head = ["Debtor", ...(hasDesigAR ? ["Designation"] : []), ...DCOLS.map(c => c.label)].map(esc).join(",");
    const rows = sortedDetail.map(r => [esc(detailDebtor), ...(hasDesigAR ? [esc(r.designation || "")] : []), ...DCOLS.map(c => esc(c.t === "date" ? fmtDate(r[c.k]) : r[c.k]))].join(","));
    const url = URL.createObjectURL(new Blob([[head, ...rows].join("\r\n")], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url; a.download = `AR_${(detailDebtor || "debtor").replace(/[^\w]+/g, "_")}_${iso(h.asofdate)}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const exportDebtors = () => {
    const esc = v => '"' + String(v == null ? "" : v).replace(/"/g, '""') + '"';
    const cols = ["Debtor", ...(debtorDesigs.length ? ["Designation"] : []), "Open Balance", "% of A/R", "0-30", "31-60", "61-90", "91-120", ">120", "Past Due", "% of Past Due", "Eligible", "% of Eligible", "Limit %", "Concentration Ineligible"];
    const arEnd = parseFloat(h.ar_ending_balance) || 0;
    const rows = shownDebtors.map(d => {
      const ob = parseFloat(d.open_balance) || 0, elig = parseFloat(d.eligible) || 0, past = parseFloat(d.past_due) || 0;
      const pctAR = arEnd ? (ob / arEnd * 100) : 0, pctPast = dtot.past ? (past / dtot.past * 100) : 0, pe = dtot.elig ? (elig / dtot.elig * 100) : 0;
      return [d.consolidatedname, ...(debtorDesigs.length ? [d.designation || ""] : []), ob, pctAR.toFixed(1), parseFloat(d.inv_0_30) || 0, parseFloat(d.inv_31_60) || 0, parseFloat(d.inv_61_90) || 0,
        parseFloat(d.inv_91_120) || 0, parseFloat(d.inv_over120) || 0, past, pctPast.toFixed(1), elig, pe.toFixed(1),
        (effLimit(d.consolidatedname) * 100).toFixed(1), concIneligFor(d.consolidatedname, elig)].map(esc).join(",");
    });
    const url = URL.createObjectURL(new Blob([[cols.map(esc).join(","), ...rows].join("\r\n")], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url; a.download = `Debtors_${h.accountid || "acct"}_${iso(h.asofdate)}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const cardTitle = { fontSize: 11, fontWeight: 700, color: B.cyanDark, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10, paddingBottom: 6, borderBottom: "1.5px solid " + B.cyanLight };
  const row = { display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 6 };
  const hint = { fontSize: 10, color: "var(--color-text-secondary)", marginBottom: 3 };
  const hCell = { padding: "8px 12px", fontSize: 12, fontWeight: 700, color: B.cyanDark, background: B.cyanLight, borderBottom: "1px solid " + B.cyan, whiteSpace: "nowrap" };
  // Totals row that sits ABOVE the column headers, matching the Debtors and
  // Vendors tables. Same amber as tfootCell, minus the sticky positioning it
  // does not need up here.
  const smTot = { fontWeight: 700, color: "#603700", borderBottom: "1px solid #E8C36A" };
  const goldC = { fontWeight: 700, color: "#603700", borderBottom: "1px solid #E8C36A" };
  // Headers wrap (TH_WRAP), data cells don't — same as the Borrower Dashboard.
  const thSticky = { fontSize: 12, fontWeight: 600, color: B.cyanDark, background: "#ffffff", ...TH_WRAP, position: "sticky", top: 0, zIndex: 3, borderBottom: "2px solid " + B.cyan, boxShadow: "0 2px 4px rgba(0,0,0,0.06)" };

  return <>
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
      <Btn onClick={() => (editing ? setEditing(false) : go("collateral-home"))}>← Borrowing Bases</Btn>
      <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
        {editing ? <>
          <Btn onClick={() => setEditing(false)} disabled={busy}>Cancel</Btn>
          <Btn variant="primary" onClick={save} disabled={busy}>{busy ? "Saving…" : "Save changes"}</Btn>
        </> : <>
          {boxHref && <a href={boxHref} target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", padding: "6px 12px", borderRadius: 6, fontSize: 13, fontWeight: 500, textDecoration: "none", border: "0.5px solid " + B.cyan, color: B.cyanDark }}>View Online</a>}
          {role?.canEdit && (existingColl
            ? <Btn onClick={openCollateralForm} title="Open the collateral entry already on this loan for this date">Open Collateral Entry</Btn>
            : <Btn variant="primary" disabled={!h.loanid} onClick={openCollateralForm} title={h.loanid ? "Review a pre-filled collateral entry, then save it on the loan" : "Assign a loan first (Edit)"}>Post to Portfolio…</Btn>)}
          {role?.canEdit && <Btn title="Edit" onClick={startEdit}><Icon type="edit" /></Btn>}
          {role?.isAdmin && <Btn title="Delete" variant="danger" onClick={remove}><Icon type="delete" /></Btn>}
        </>}
      </div>
    </div>
    <SectionHeader title={(h.companyname || h.accountid) + " — Borrowing Base"} />

    {editing
      ? <Card style={{ padding: "12px 16px", marginBottom: 16, borderLeft: "3px solid " + B.cyan, display: "flex", gap: 24, flexWrap: "wrap", alignItems: "end" }}>
          <div style={{ minWidth: 150 }}><div style={hint}>AS-OF DATE</div><FInput type="date" value={form.asofdate ?? ""} onChange={v => setF("asofdate", v)} /></div>
          <div style={{ minWidth: 150 }}><div style={hint}>PRIOR AS-OF DATE</div><FInput type="date" value={form.previousasofdate ?? ""} onChange={v => setF("previousasofdate", v)} /></div>
          <div style={{ minWidth: 180 }}><div style={hint}>LOAN (applied to)</div>
            <FSelect value={form.loanid ?? ""} onChange={v => setF("loanid", v)}
              options={[{ value: "", label: "— Select loan —" }, ...activeLoans.map(l => ({ value: String(l.id), label: (l.loannumber || l.id) + (l.facilitytype ? ` · ${l.facilitytype}` : "") + (l.loanstatus ? ` · ${l.loanstatus}` : "") }))]} /></div>
          <div style={{ minWidth: 150 }}><div style={hint}>CONCENTRATION LIMIT (percent · 30 = 30%)</div><FInput value={form.concentration_limit ?? ""} onChange={v => setF("concentration_limit", v)} placeholder="30" /></div>
          <div style={{ fontSize: 11, color: "var(--color-text-secondary)", flex: 1, minWidth: 220 }}>
            Editing the certificate figures as-signed — nothing is recomputed. A/R aging, debtors and concentration below are rolled up from the AR line detail and aren't edited here.
          </div>
        </Card>
      : <div style={{ fontSize: 13, color: "var(--color-text-secondary)", marginTop: -8, marginBottom: 16 }}>
          As of <b style={{ color: "var(--color-text-primary)" }}>{fmtDate(h.asofdate)}</b>
          {loanLabel ? <> · loan <b style={{ color: "var(--color-text-primary)" }}>{loanLabel}</b></> : <span style={{ color: B.amberDark }}> · no loan assigned</span>}
          {h.previousasofdate ? ` · prior ${fmtDate(h.previousasofdate)}` : ""}
          {h.filename ? ` · ${h.filename}` : ""}
          {h.uploadedby ? ` · uploaded by ${h.uploadedby}` : ""}
        </div>}

    {saveErr && <div style={{ marginBottom: 14 }}><ErrorMsg msg={saveErr} /></div>}
    {postErr && <div style={{ marginBottom: 14, padding: "10px 12px", borderRadius: 6, fontSize: 13, background: "#FAEEDA", color: "#8a5a00", border: "1px solid #E8C36A" }}>⚠️ {postErr}</div>}
    {existingColl && <div style={{ marginBottom: 14, padding: "10px 12px", borderRadius: 6, fontSize: 13, background: "#EAF7FB", color: B.cyanDark, border: "1px solid " + B.cyanLight, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
      A collateral entry already exists for loan <b>{loanLabel}</b> as of {fmtDate(h.asofdate)}.
      {role?.canEdit && <a onClick={openCollateralForm} style={{ color: B.cyan, cursor: "pointer", fontWeight: 600 }}>Open collateral entry →</a>}
    </div>}

    {/* ── Tabs (same structure as the Portfolio Management loan page) ── */}
    <div style={{ marginTop: 16 }}>
    <div style={{ display: "flex", flexWrap: "wrap", gap: 3, paddingLeft: 0, position: "relative", zIndex: 1, marginTop: 4, borderBottom: "4px solid " + B.cyan }}>
      {/* The slow-moving tab appears ONLY for certificates that carry the data.
          Most borrowers never report it, and an always-present tab that is
          empty four times out of five trains people to ignore it. */}
      {[["overview", "Overview"], ["ar", "A/R Detail"], ["ap", "A/P Detail"],
        ...(hasInv ? [["inv", "Inventory"]] : []),
      ...(hasSlowMoving ? [["sm", "Slow Moving Inventory"]] : [])].map(([k, lbl]) => (
        <TabBtn key={k} id={k} current={tab} label={lbl} onClick={setTab} />
      ))}
    </div>
    <div style={{ background: "var(--color-background-primary)", border: "0.5px solid var(--color-border-tertiary)", borderTop: "none", borderRadius: "0 0 8px 8px", padding: 16, position: "relative", zIndex: 2 }}>

    {/* ══════════ OVERVIEW — Certificate + Inventory ══════════ */}
    {/* ══════════ OVERVIEW — Certificate · A/R Trend · Inventory ══════════ */}
    {tab === "overview" && (
      <div style={{ display: "grid", gridTemplateColumns: (hist.length > 1 || (hasInv && inv.length > 0)) ? "2fr 3fr" : "1fr", gap: 16, marginBottom: 16, alignItems: "start" }}>
                      <Card style={{ padding: 16, overflowX: "auto" }}>
        <div style={cardTitle}>Certificate</div>
        <table style={{ width: "100%", borderCollapse: "collapse", whiteSpace: "nowrap" }}>
          <tbody>
            {certGrouped.map((g, i) => {
              if (g.header) return (
                <tr key={"s" + i}><td colSpan={2} style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: B.cyanDark, background: B.cyanLight, padding: "6px 8px" }}>{g.header}</td></tr>
              );
              const l = g.line;
              const strong = /total|net available|gross collateral|borrowing base|current loan balance|excess/i.test(l.label || "");
              const isRate = /advance rate|nolv|discount|%/i.test(l.label || "") && l.value != null && Math.abs(l.value) <= 1;
              return (
                <tr key={i}>
                  <td style={{ fontSize: 12, padding: "3px 8px 3px 0", color: strong ? "var(--color-text-primary)" : "var(--color-text-secondary)", fontWeight: strong ? 600 : 400 }}>{l.label}</td>
                  <td style={{ fontSize: 12, padding: "3px 0", textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: strong ? 600 : 400, minWidth: editing ? 130 : 0 }}>
                    {editing
                      ? <FInputNum value={lineEdits[l.id] ?? ""} onChange={v => setLineEdits(m => ({ ...m, [l.id]: v }))} />
                      : (l.value == null ? "" : (isRate ? fmtRatio(l.value) : fmt(l.value)))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

        {(hist.length > 1 || (hasInv && inv.length > 0)) && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>
                                {hist.length > 1 && (
          <Card style={{ opacity: editing ? 0.6 : 1 }}>
            <div style={{ ...cardTitle, padding: "12px 12px 8px", marginBottom: 0 }}>A/R Trend <span style={{ fontWeight: 400, color: "var(--color-text-secondary)" }}>({hist.length})</span></div>

            {/* ── Nested-subset bar chart (oldest → newest) ── */}
            <div style={{ padding: "8px 12px 4px" }}>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
                {[...TSER, { id: "excess", label: "Excess / (Deficit)", color: "#BA7517", line: true }].map(s => (
                  <button key={s.id} onClick={() => setTrendOn(o => ({ ...o, [s.id]: !o[s.id] }))}
                    style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 8px", borderRadius: 12, border: "0.5px solid var(--color-border-secondary)", background: "none", cursor: "pointer", fontSize: 11, color: "var(--color-text-secondary)", opacity: trendOn[s.id] ? 1 : 0.4 }}>
                    <span style={{ width: s.line ? 14 : 10, height: s.line ? 3 : 10, borderRadius: 2, background: s.color, display: "inline-block" }} />
                    {s.label}
                  </button>
                ))}
              </div>
              <ArExcessTrend hist={hist} series={TSER.filter(s => trendOn[s.id])} showExcess={trendOn.excess} />
            </div>

            <div style={{ maxHeight: 220, overflowY: "auto", overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, whiteSpace: "nowrap" }}>
                <thead><tr>
                  <th style={{ ...thSticky, textAlign: "left" }}>As-of</th>
                  <th style={{ ...thSticky, textAlign: "right" }}>Ending A/R</th>
                  <th style={{ ...thSticky, textAlign: "right" }}>Eligible A/R</th>
                  <th style={{ ...thSticky, textAlign: "right" }}>Net Avail A/R</th>
                  <th style={{ ...thSticky, textAlign: "right" }}>Borrowing Base Avail</th>
                  <th style={{ ...thSticky, textAlign: "right" }}>Loan Balance</th>
                  <th style={{ ...thSticky, textAlign: "right" }}>Excess / (Deficit)</th>
                </tr></thead>
                <tbody>
                  {hist.map(r => {
                    const ex = parseFloat(r.excess_availability);
                    return (
                      <tr key={r.id} style={{ cursor: editing ? "default" : "pointer", background: r.id === h.id ? B.cyanLight : "" }} onClick={() => { if (!editing) go("collateral-bbc", { bbid: r.id }); }}>
                        <Td style={{ fontWeight: r.id === h.id ? 600 : 400 }}>{fmtDate(r.asofdate)}</Td>
                        <TdNum>{fmt(r.ar_ending_balance || 0)}</TdNum>
                        <TdNum>{fmt(r.ar_total_eligible || 0)}</TdNum>
                        <TdNum>{fmt(r.ar_net_available || 0)}</TdNum>
                        <TdNum>{fmt(r.borrowing_base_avail || 0)}</TdNum>
                        <TdNum>{fmt(r.current_loan_balance || 0)}</TdNum>
                        <TdNum style={{ color: ex < 0 ? "#993C1D" : "inherit" }}>{fmt(r.excess_availability || 0)}</TdNum>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )}

                            {hasInv && inv.length > 0 && (
          <Card style={{ overflowX: "auto" }}>
            <div style={{ ...cardTitle, padding: "12px 12px 8px", marginBottom: 0 }}>Inventory by Category{editing ? <span style={{ fontWeight: 400, fontSize: 12, color: "var(--color-text-secondary)" }}> · editing</span> : ""}</div>
            <table style={{ width: "100%", borderCollapse: "collapse", whiteSpace: "nowrap" }}>
              <thead>
                <tr style={{ background: "#FDF0D5" }}>
                  <Td style={goldC}>Total (eligible)</Td>
                  <Td style={goldC}></Td>
                  <TdNum style={goldC}>{fmt(invTot.g)}</TdNum>
                  <TdNum style={goldC}>{fmt(invTot.i)}</TdNum>
                  <TdNum style={goldC}>{fmt(invTot.e)}</TdNum>
                  <TdNum style={goldC}></TdNum>
                  <TdNum style={goldC}>{fmt(invTot.av)}</TdNum>
                </tr>
                <tr>
                  <th style={{ ...hCell, textAlign: "left" }}>Bucket</th>
                  <th style={{ ...hCell, textAlign: "left" }}>Category</th>
                  <th style={{ ...hCell, textAlign: "right" }}>Gross</th>
                  <th style={{ ...hCell, textAlign: "right" }}>Ineligible</th>
                  <th style={{ ...hCell, textAlign: "right" }}>Eligible</th>
                  <th style={{ ...hCell, textAlign: "right" }}>Adv Rate</th>
                  <th style={{ ...hCell, textAlign: "right" }}>Available</th>
                </tr>
              </thead>
              <tbody>
                {invByBucket.map((grp, gi) => [
                  ...grp.rows.map((c, i) => {
                    const e = invEdits[c.colnum] || {};
                    const setIe = (field, v) => setInvEdits(m => ({ ...m, [c.colnum]: { ...(m[c.colnum] || {}), [field]: v } }));
                    return (
                    <tr key={gi + "-" + i} style={{ opacity: (!editing && c.excluded) ? 0.6 : 1 }}
                      onMouseEnter={ev => ev.currentTarget.style.background = "var(--color-background-secondary)"} onMouseLeave={ev => ev.currentTarget.style.background = ""}>
                      <Td>{editing
                        ? <div style={{ minWidth: 130 }}><FSelect value={e.category_norm ?? ""} onChange={v => setIe("category_norm", v)}
                            options={[{ value: "", label: "—" }, ...INV_ORDER.map(b => ({ value: b, label: b }))]} /></div>
                        : <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 600, background: B.cyanLight, color: B.cyanDark }}>{c.category_norm || "—"}</span>}</Td>
                      <Td style={{ fontWeight: 500 }}>{editing
                        ? <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <div style={{ minWidth: 180 }}><FInput value={e.category_label ?? ""} onChange={v => setIe("category_label", v)} placeholder="Category name" /></div>
                            <label style={{ fontWeight: 400, fontSize: 11, color: "var(--color-text-secondary)", whiteSpace: "nowrap" }}><input type="checkbox" checked={!!e.excluded} onChange={ev => setIe("excluded", ev.target.checked)} style={{ verticalAlign: "middle", marginRight: 3 }} />exclude</label>
                          </div>
                        : <>{c.category_label}{c.excluded ? <span style={{ color: "var(--color-text-secondary)", fontWeight: 400 }}> · excluded</span> : ""}</>}</Td>
                      <TdNum>{editing ? <FInputNum value={e.gross ?? ""} onChange={v => setIe("gross", v)} /> : fmt(c.gross || 0)}</TdNum>
                      <TdNum>{editing ? <FInputNum value={e.ineligible ?? ""} onChange={v => setIe("ineligible", v)} /> : fmt(c.ineligible || 0)}</TdNum>
                      <TdNum>{editing ? <FInputNum value={e.eligible ?? ""} onChange={v => setIe("eligible", v)} /> : fmt(c.eligible || 0)}</TdNum>
                      <TdNum>{editing ? <FInputNum value={e.advance_rate ?? ""} onChange={v => setIe("advance_rate", v)} /> : (c.advance_rate != null ? fmtRatio(c.advance_rate) : "—")}</TdNum>
                      <TdNum>{editing ? <FInputNum value={e.available ?? ""} onChange={v => setIe("available", v)} /> : fmt(c.available || 0)}</TdNum>
                    </tr>
                    );
                  }),
                  (invHasSub && grp.rows.some(r => !r.excluded)) ? (
                    <tr key={gi + "-sub"} style={{ background: "#EAF7FB" }}>
                      <Td style={{ fontWeight: 600, color: B.cyanDark }}></Td>
                      <Td style={{ fontWeight: 600, color: B.cyanDark }}>{grp.norm} subtotal</Td>
                      <TdNum style={{ fontWeight: 600, color: B.cyanDark }}>{fmt(grp.g)}</TdNum>
                      <TdNum style={{ fontWeight: 600, color: B.cyanDark }}>{fmt(grp.i)}</TdNum>
                      <TdNum style={{ fontWeight: 600, color: B.cyanDark }}>{fmt(grp.e)}</TdNum>
                      <TdNum style={{ fontWeight: 600, color: B.cyanDark }}></TdNum>
                      <TdNum style={{ fontWeight: 600, color: B.cyanDark }}>{fmt(grp.av)}</TdNum>
                    </tr>
                  ) : null
                ])}
                {editing && newInv.map(r => (
                  <tr key={r.tempId} style={{ background: "#F3FBFD" }}>
                    <Td>
                      <div style={{ minWidth: 130 }}><FSelect value={r.category_norm ?? ""} onChange={v => setNewIe(r.tempId, "category_norm", v)}
                        options={[{ value: "", label: "—" }, ...INV_ORDER.map(b => ({ value: b, label: b }))]} /></div>
                    </Td>
                    <Td>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ minWidth: 180 }}><FInput value={r.category_label ?? ""} onChange={v => setNewIe(r.tempId, "category_label", v)} placeholder="New category name" /></div>
                        <label style={{ fontWeight: 400, fontSize: 11, color: "var(--color-text-secondary)", whiteSpace: "nowrap" }}><input type="checkbox" checked={!!r.excluded} onChange={ev => setNewIe(r.tempId, "excluded", ev.target.checked)} style={{ verticalAlign: "middle", marginRight: 3 }} />exclude</label>
                        <button type="button" onClick={() => removeNewRow(r.tempId)} title="Remove row" style={{ border: "none", background: "none", color: "#993C1D", cursor: "pointer", fontSize: 15, lineHeight: 1, padding: "0 2px" }}>×</button>
                      </div>
                    </Td>
                    <TdNum><FInputNum value={r.gross ?? ""} onChange={v => setNewIe(r.tempId, "gross", v)} /></TdNum>
                    <TdNum><FInputNum value={r.ineligible ?? ""} onChange={v => setNewIe(r.tempId, "ineligible", v)} /></TdNum>
                    <TdNum><FInputNum value={r.eligible ?? ""} onChange={v => setNewIe(r.tempId, "eligible", v)} /></TdNum>
                    <TdNum><FInputNum value={r.advance_rate ?? ""} onChange={v => setNewIe(r.tempId, "advance_rate", v)} /></TdNum>
                    <TdNum><FInputNum value={r.available ?? ""} onChange={v => setNewIe(r.tempId, "available", v)} /></TdNum>
                  </tr>
                ))}
              </tbody>
            </table>
            {editing && (
              <div style={{ padding: "8px 12px" }}>
                <Btn onClick={addInvRow}>+ Add category</Btn>
              </div>
            )}
          </Card>
        )}

          </div>
        )}
      </div>
    )}

    {/* ══════════ INVENTORY — composition, contribution, and the mix over time ══
        The certificate's own inventory numbers live on the Overview tab, where
        they can be edited. This tab is the other question: not "what does this
        certificate say" but "what has been happening". Nothing here is
        editable, and nothing here duplicates that table. */}
    {/* Inventory — the same component the Borrower Dashboard renders (collateralCards.js) */}
    {tab === "inv" && <InventoryTab invTrend={invTrend} hist={hist} skus={partyTrend.skus} cur={h} onOpen={id => { if (!editing) go("collateral-bbc", { bbid: id }); }} onSlowMoving={hasSlowMoving ? () => setTab("sm") : null} />}

    {/* ══════════ A/R DETAIL — A/R Aging + A/R Aging Trend · Debtors ══════════ */}
    {tab === "ar" && (
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "start" }}>
          <div style={{ flex: "0 1 380px", minWidth: 280 }}>
                                <Card style={{ padding: 16, opacity: editing ? 0.6 : 1 }}>
          <div style={cardTitle}>A/R Aging (invoice date)</div>
          {agingTotal <= 0
            ? <div style={{ fontSize: 13, color: "var(--color-text-secondary)" }}>No aging detail.</div>
            : <>
              <div style={{ display: "flex", height: 12, borderRadius: 6, overflow: "hidden", marginBottom: 14, background: "var(--color-background-secondary)" }}>
                {AGING.map(b => { const v = parseFloat(ag[b.key]) || 0; return v > 0 && <div key={b.key} style={{ width: (v / agingTotal * 100) + "%", background: b.color }} title={`${b.label}: ${fmt(v)}`} />; })}
              </div>
              {AGING.map(b => {
                const v = parseFloat(ag[b.key]) || 0;
                return (
                  <div key={b.key} style={{ ...row, alignItems: "center" }}>
                    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ width: 10, height: 10, borderRadius: 2, background: b.color, display: "inline-block" }} />
                      {b.label}
                    </span>
                    <span><b>{fmt(v)}</b> <span style={{ color: "var(--color-text-secondary)" }}>({Math.round(v / agingTotal * 100)}%)</span></span>
                  </div>
                );
              })}
              <div style={{ ...row, borderTop: "1px solid var(--color-border-tertiary)", paddingTop: 8, marginTop: 4 }}>
                <span style={{ color: "var(--color-text-secondary)" }}>Past due (signed)</span><b>{fmt(ag.pastdue || 0)}</b>
              </div>
            </>}
        </Card>

          </div>
          <div style={{ flex: 1, minWidth: 320 }}>
                            {hist.length > 1 && (
          <Card style={{ opacity: editing ? 0.6 : 1 }}>
            <div style={{ ...cardTitle, padding: "12px 12px 8px", marginBottom: 0 }}>A/R Aging Trend <span style={{ fontWeight: 400, color: "var(--color-text-secondary)" }}>({hist.length})</span></div>
            <div style={{ padding: "8px 12px 4px" }}>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
                {AGING.map(b => (
                  <button key={b.key} onClick={() => setTrendArAgeOn(o => ({ ...o, [b.key]: !o[b.key] }))}
                    style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 8px", borderRadius: 12, border: "0.5px solid var(--color-border-secondary)", background: "none", cursor: "pointer", fontSize: 11, color: "var(--color-text-secondary)", opacity: trendArAgeOn[b.key] ? 1 : 0.4 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 2, background: b.color, display: "inline-block" }} />
                    {b.label}
                  </button>
                ))}
              </div>
              <StackTrend rows={hist.slice().reverse()} buckets={AGING.filter(b => trendArAgeOn[b.key])} value={(r, b) => parseFloat(r["ar_" + b.key]) || 0} />
            </div>
            <div style={{ maxHeight: 220, overflowY: "auto", overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, whiteSpace: "nowrap" }}>
                <thead><tr>
                  <th style={{ ...thSticky, textAlign: "left" }}>As-of</th>
                  {AGING.map(b => <th key={b.key} style={{ ...thSticky, textAlign: "right" }}>{b.label}</th>)}
                  <th style={{ ...thSticky, textAlign: "right" }}>Total</th>
                </tr></thead>
                <tbody>
                  {hist.map(r => {
                    const tot = AGING.reduce((s, b) => s + (parseFloat(r["ar_" + b.key]) || 0), 0);
                    return (
                      <tr key={r.id} style={{ cursor: editing ? "default" : "pointer", background: r.id === h.id ? B.cyanLight : "" }} onClick={() => { if (!editing) go("collateral-bbc", { bbid: r.id }); }}>
                        <Td style={{ fontWeight: r.id === h.id ? 600 : 400 }}>{fmtDate(r.asofdate)}</Td>
                        {AGING.map(b => { const v = parseFloat(r["ar_" + b.key]) || 0; return <TdNum key={b.key}>{fmt(v)}<span style={{ fontSize: 10, color: "var(--color-text-secondary)" }}> ({tot ? (v / tot * 100).toFixed(1) : "0.0"}%)</span></TdNum>; })}
                        <TdNum style={{ fontWeight: 600 }}>{fmt(tot)}</TdNum>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )}

          </div>
        </div>

        {/* A/R Trend and Rollforward A/R — same cards as the Borrower Dashboard (collateralCards.js) */}
        {/* MoM / QoQ / YoY and the roll (step-down), as of THIS certificate — arTrendCards.js */}
        <ArPeriodCard hist={hist.filter(r => String(r.asofdate || "").slice(0, 10) <= String(h.asofdate || "").slice(0, 10))} />
        <ArRollCard accountid={h.accountid} asOf={String(h.asofdate || "").slice(0, 10)} />
        <ArTrendCard hist={hist} currentId={h.id} onOpen={id => { if (!editing) go("collateral-bbc", { bbid: id }); }} />
        <ArRollforwardCard hist={hist} currentId={h.id} onOpen={id => { if (!editing) go("collateral-bbc", { bbid: id }); }} />
        <ArQualityCard hist={hist} currentId={h.id} onOpen={id => { if (!editing) go("collateral-bbc", { bbid: id }); }} />

        {/* Top 10 Debtors — open A/R trend (top 10 from the most recent BBC) */}
        {partyTrendCard("Top 10 Debtors — Open A/R", debtorTrend, topDebtorOff, setTopDebtorOff, "Total A/R")}

            <Card style={{ overflowX: "auto", marginBottom: 16, opacity: editing ? 0.6 : 1 }}>
      <div style={{ ...cardTitle, padding: "12px 12px 8px", marginBottom: 0, display: "flex", alignItems: "center", gap: 10 }}>
        <span>Debtors — Open A/R &amp; Concentration <span style={{ fontWeight: 400, color: "var(--color-text-secondary)" }}>({shownDebtors.length}{debtorDesig ? ` of ${debtors.length}` : ""}, grain: {ag.grain || "—"} · default limit {(climit * 100).toFixed(1)}%{invoiceGrain ? " · click a debtor for invoices" : ""})</span></span>
        <span style={{ marginLeft: "auto", display: "flex", gap: 8, textTransform: "none", alignItems: "center" }}>
          {debtorDesigs.length > 0 && <select value={debtorDesig} onChange={e => setDebtorDesig(e.target.value)}
            style={{ padding: "6px 10px", fontSize: 12, borderRadius: 6, border: "0.5px solid var(--color-border-secondary)", background: "var(--color-background-primary)", color: "var(--color-text-primary)" }}>
            <option value="">All designations</option>
            {debtorDesigs.map(dg => <option key={dg} value={dg}>{dg}</option>)}
          </select>}
          {!editing && role?.canEdit && (editLimits ? <>
            <Btn onClick={() => setEditLimits(false)} disabled={savingLim}>Cancel</Btn>
            <Btn variant="primary" onClick={saveLimits} disabled={savingLim}>{savingLim ? "Saving…" : "Save limits"}</Btn>
          </> : <Btn onClick={startEditLimits}>Edit debtor limits</Btn>)}
          {!editLimits && debtors.length > 0 && <Btn onClick={exportDebtors}>⬇ Export CSV</Btn>}
        </span>
      </div>
      {editLimits && <div style={{ fontSize: 11, color: "var(--color-text-secondary)", padding: "0 12px 8px" }}>Enter each debtor's limit as a percent (e.g. <b>40</b> = 40%). Blank uses the account default ({(climit * 100).toFixed(0)}%). Saved per debtor for this account and carried forward to future BBCs.</div>}
      <RowCapScroll>
      <table style={{ width: "100%", borderCollapse: "collapse", whiteSpace: "nowrap" }}>
        <thead style={{ position: "sticky", top: 0, zIndex: 3, background: "#ffffff" }}>
          <tr style={{ background: "#FDF0D5" }}>
            <Td style={{ fontWeight: 700, color: "#603700", borderBottom: "1px solid #E8C36A" }}>Total ({shownDebtors.length})</Td>
            {debtorDesigs.length > 0 && <Td style={{ fontWeight: 700, color: "#603700", borderBottom: "1px solid #E8C36A" }}>{debtorDesig || "All"}</Td>}
            <TdNum style={{ fontWeight: 700, color: "#603700", borderBottom: "1px solid #E8C36A" }}>{fmt(dtot.open)}</TdNum>
            <TdNum style={{ fontWeight: 700, color: "#603700", borderBottom: "1px solid #E8C36A" }}>{(dtotPct * 100).toFixed(1)}%</TdNum>
            {DBKT.map(b => <TdNum key={b.key} style={{ fontWeight: 700, color: "#603700", borderBottom: "1px solid #E8C36A" }}>{fmt(dtot[b.tot])}</TdNum>)}
            <TdNum style={{ fontWeight: 700, color: "#603700", borderBottom: "1px solid #E8C36A" }}>{fmt(dtot.past)}</TdNum>
            <TdNum style={{ fontWeight: 700, color: "#603700", borderBottom: "1px solid #E8C36A" }}>{dtot.past ? "100.0%" : "—"}</TdNum>
            <TdNum style={{ fontWeight: 700, color: "#603700", borderBottom: "1px solid #E8C36A" }}>{fmt(dtot.elig)}</TdNum>
            <TdNum style={{ fontWeight: 700, color: "#603700", borderBottom: "1px solid #E8C36A" }}>{dtot.elig ? "100.0%" : "—"}</TdNum>
            <TdNum style={{ fontWeight: 700, color: "#603700", borderBottom: "1px solid #E8C36A" }}><span style={{ fontSize: 11, fontWeight: 600 }}>default {(climit * 100).toFixed(1)}%</span></TdNum>
            <TdNum style={{ fontWeight: 700, color: "#603700", borderBottom: "1px solid #E8C36A" }}>{dtotConcInelig > 0 ? fmt(dtotConcInelig) : "—"}</TdNum>
          </tr>
          <tr>
            <th style={{ ...hCell, textAlign: "left" }}>Debtor (Consolidated)</th>
            {debtorDesigs.length > 0 && <th style={{ ...hCell, textAlign: "left" }}>Designation</th>}
            <th style={{ ...hCell, textAlign: "right" }}>Open Balance</th>
            <th style={{ ...hCell, textAlign: "right" }}>% of A/R</th>
            {DBKT.map(b => <th key={b.key} style={{ ...hCell, textAlign: "right" }}>{b.label}</th>)}
            <th style={{ ...hCell, textAlign: "right" }}>Past Due</th>
            <th style={{ ...hCell, textAlign: "right" }}>% of Past Due</th>
            <th style={{ ...hCell, textAlign: "right" }}>Eligible</th>
            <th style={{ ...hCell, textAlign: "right" }}>% of Eligible</th>
            <th style={{ ...hCell, textAlign: "right" }}>Limit</th>
            <th style={{ ...hCell, textAlign: "right" }}>Concentration Ineligible</th>
          </tr>
        </thead>
        <tbody>
          {shownDebtors.map((d, i) => {
            const pct = h.ar_ending_balance ? (parseFloat(d.open_balance) / parseFloat(h.ar_ending_balance)) : 0;
            const elig = parseFloat(d.eligible) || 0;
            const pe = dtot.elig ? elig / dtot.elig : 0;
            const L = effLimit(d.consolidatedname);
            const isOverride = limMap[d.consolidatedname] != null && !isNaN(limMap[d.consolidatedname]);
            const over = pe > L;
            const ci = concIneligFor(d.consolidatedname, elig);
            return (
              <tr key={i} style={{ cursor: (invoiceGrain && !editLimits) ? "pointer" : "default" }}
                onClick={() => { if (invoiceGrain && !editLimits) { setDsort({ key: "invoicedate", dir: "asc" }); setDetailDebtor(d.consolidatedname); } }}
                onMouseEnter={e => e.currentTarget.style.background = "var(--color-background-secondary)"} onMouseLeave={e => e.currentTarget.style.background = ""}>
                <Td style={{ fontWeight: 500, color: (invoiceGrain && !editLimits) ? B.cyanDark : undefined }}>{d.consolidatedname}</Td>
                {debtorDesigs.length > 0 && <Td>{d.designation || "—"}</Td>}
                <TdNum>{fmt(d.open_balance || 0)}</TdNum>
                <TdNum>{(pct * 100).toFixed(1)}%</TdNum>
                {DBKT.map(b => <TdNum key={b.key}>{fmt(d[b.key] || 0)}</TdNum>)}
                <TdNum>{fmt(d.past_due || 0)}</TdNum>
                <TdNum>{dtot.past ? (parseFloat(d.past_due || 0) / dtot.past * 100).toFixed(1) + "%" : "—"}</TdNum>
                <TdNum>{fmt(elig)}</TdNum>
                <TdNum>{dtot.elig ? <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 4, fontSize: 12, fontWeight: 600, background: over ? "#FAECE7" : "#E1F5EE", color: over ? "#993C1D" : "#0F6E56" }}>{(pe * 100).toFixed(1)}%</span> : "—"}</TdNum>
                <TdNum>{editLimits
                  ? <input type="number" step="0.1" value={limEdits[d.consolidatedname] ?? ""} onChange={e => setLimEdits(m => ({ ...m, [d.consolidatedname]: e.target.value }))} placeholder={(climit * 100).toFixed(0)}
                      style={{ width: 66, boxSizing: "border-box", padding: "4px 6px", fontSize: 12, borderRadius: 4, border: "1px solid #999", textAlign: "right" }} />
                  : <span title={isOverride ? "Debtor-specific limit" : "Account default"} style={{ fontWeight: isOverride ? 700 : 400, color: isOverride ? "#603700" : "var(--color-text-secondary)" }}>{(L * 100).toFixed(1)}%</span>}
                </TdNum>
                <TdNum style={{ color: ci > 0 ? "#993C1D" : "inherit", fontWeight: ci > 0 ? 600 : 400 }}>{ci > 0 ? fmt(ci) : "—"}</TdNum>
              </tr>
            );
          })}
        </tbody>
      </table>
      </RowCapScroll>
    </Card>

      </div>
    )}

    {/* ══════════ A/P DETAIL — Aging · Aging Trend · A/P by Vendor ══════════ */}
    {tab === "ap" && (
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "start" }}>
          <div style={{ flex: "0 1 380px", minWidth: 280 }}>
            {hasAP
              ?             <Card style={{ padding: 16, opacity: editing ? 0.6 : 1 }}>
              <div style={cardTitle}>A/P Aging (due date)</div>
              {apAgingTotal <= 0
                ? <div style={{ fontSize: 13, color: "var(--color-text-secondary)" }}>No A/P aging detail.</div>
                : <>
                  <div style={{ display: "flex", height: 12, borderRadius: 6, overflow: "hidden", marginBottom: 14, background: "var(--color-background-secondary)" }}>
                    {APAGING.map(b => { const v = parseFloat(h[b.key]) || 0; return v > 0 && <div key={b.key} style={{ width: (v / apAgingTotal * 100) + "%", background: b.color }} title={`${b.label}: ${fmt(v)}`} />; })}
                  </div>
                  {APAGING.map(b => {
                    const v = parseFloat(h[b.key]) || 0;
                    return (
                      <div key={b.key} style={{ ...row, alignItems: "center" }}>
                        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ width: 10, height: 10, borderRadius: 2, background: b.color, display: "inline-block" }} />
                          {b.label}
                        </span>
                        <span><b>{fmt(v)}</b> <span style={{ color: "var(--color-text-secondary)" }}>({apAgingTotal ? Math.round(v / apAgingTotal * 100) : 0}%)</span></span>
                      </div>
                    );
                  })}
                </>}
            </Card>

              : <Card style={{ padding: 16 }}><div style={cardTitle}>A/P Aging (due date)</div><div style={{ fontSize: 13, color: "var(--color-text-secondary)" }}>No A/P Waterfall on this certificate.</div></Card>}
          </div>
          <div style={{ flex: 1, minWidth: 320 }}>
                    {hasAP && hist.length > 1 && (
          <Card style={{ opacity: editing ? 0.6 : 1 }}>
            <div style={{ ...cardTitle, padding: "12px 12px 8px", marginBottom: 0 }}>A/P Aging Trend <span style={{ fontWeight: 400, color: "var(--color-text-secondary)" }}>({hist.length})</span></div>
            <div style={{ padding: "8px 12px 4px" }}>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
                {APAGING.map(b => (
                  <button key={b.key} onClick={() => setTrendApOn(o => ({ ...o, [b.key]: !o[b.key] }))}
                    style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 8px", borderRadius: 12, border: "0.5px solid var(--color-border-secondary)", background: "none", cursor: "pointer", fontSize: 11, color: "var(--color-text-secondary)", opacity: trendApOn[b.key] ? 1 : 0.4 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 2, background: b.color, display: "inline-block" }} />
                    {b.label}
                  </button>
                ))}
              </div>
              <StackTrend rows={hist.slice().reverse()} buckets={APAGING.filter(b => trendApOn[b.key])} value={(r, b) => parseFloat(r[b.key]) || 0} />
            </div>
            {/* What changed — the A/P aging mix shifting out (same rules as the Insights checks AP aging shift / drift) */}
            <ApAgingFindings hist={hist} asOf={h.asofdate} th={apTh} />
            <div style={{ maxHeight: 220, overflowY: "auto", overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, whiteSpace: "nowrap" }}>
                <thead><tr>
                  <th style={{ ...thSticky, textAlign: "left" }}>As-of</th>
                  {APAGING.map(b => <th key={b.key} style={{ ...thSticky, textAlign: "right" }}>{b.label}</th>)}
                  <th style={{ ...thSticky, textAlign: "right" }}>Total</th>
                </tr></thead>
                <tbody>
                  {hist.map(r => {
                    const tot = APAGING.reduce((s, b) => s + (parseFloat(r[b.key]) || 0), 0);
                    return (
                      <tr key={r.id} style={{ cursor: editing ? "default" : "pointer", background: r.id === h.id ? B.cyanLight : "" }} onClick={() => { if (!editing) go("collateral-bbc", { bbid: r.id }); }}>
                        <Td style={{ fontWeight: r.id === h.id ? 600 : 400 }}>{fmtDate(r.asofdate)}</Td>
                        {APAGING.map(b => { const v = parseFloat(r[b.key]) || 0; return <TdNum key={b.key}>{fmt(v)}<span style={{ fontSize: 10, color: "var(--color-text-secondary)" }}> ({tot ? (v / tot * 100).toFixed(1) : "0.0"}%)</span></TdNum>; })}
                        <TdNum style={{ fontWeight: 600 }}>{fmt(tot)}</TdNum>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )}

          </div>
        </div>

        {/* Top 10 Vendors — open A/P trend (top 10 from the most recent BBC) */}
        {partyTrendCard("Top 10 Vendors — Open A/P", vendorTrend, topVendorOff, setTopVendorOff, "Total A/P")}

        {/* ── A/P by Vendor (open A/P by due-date aging) ── */}
        <Card style={{ overflowX: "auto", opacity: editing ? 0.6 : 1 }}>
          <div style={{ ...cardTitle, padding: "12px 12px 8px", marginBottom: 0, display: "flex", alignItems: "center", gap: 10 }}>
            <span>A/P by Vendor <span style={{ fontWeight: 400, color: "var(--color-text-secondary)" }}>({shownVendors.length}{vendorDesig ? ` of ${byVendorAP.length}` : ""}{(apLines && apLines.length) ? " \u00b7 click a vendor for invoices" : ""})</span></span>
            <span style={{ marginLeft: "auto", display: "flex", gap: 8, textTransform: "none", alignItems: "center" }}>
              {vendorDesigs.length > 0 && <select value={vendorDesig} onChange={e => setVendorDesig(e.target.value)}
                style={{ padding: "6px 10px", fontSize: 12, borderRadius: 6, border: "0.5px solid var(--color-border-secondary)", background: "var(--color-background-primary)", color: "var(--color-text-primary)" }}>
                <option value="">All designations</option>
                {vendorDesigs.map(dg => <option key={dg} value={dg}>{dg}</option>)}
              </select>}
              {byVendorAP.length > 0 && <Btn onClick={exportVendorsAP}>\u2b07 Export CSV</Btn>}
            </span>
          </div>
          {byVendorAP.length === 0
            ? <div style={{ padding: 20, textAlign: "center", color: "var(--color-text-secondary)", fontSize: 13 }}>No A/P line detail on this certificate.</div>
            : <table style={{ width: "100%", borderCollapse: "collapse", whiteSpace: "nowrap" }}>
              <thead>
                <tr style={{ background: "#FDF0D5" }}>
                  <Td style={goldC}>Total ({shownVendors.length})</Td>
                  {vendorDesigs.length > 0 && <Td style={goldC}>{vendorDesig || "All"}</Td>}
                  <TdNum style={goldC}>{fmt(apVendTot.open)}</TdNum>
                  <TdNum style={goldC}>{apVendTot.open ? "100.0%" : "\u2014"}</TdNum>
                  {APBKT.map(b => <TdNum key={b.key} style={goldC}>{fmt(apVendTot[b.key])}</TdNum>)}
                </tr>
                <tr>
                  <th style={{ ...hCell, textAlign: "left" }}>Vendor (Consolidated)</th>
                  {vendorDesigs.length > 0 && <th style={{ ...hCell, textAlign: "left" }}>Designation</th>}
                  <th style={{ ...hCell, textAlign: "right" }}>Open Balance</th>
                  <th style={{ ...hCell, textAlign: "right" }}>% of A/P</th>
                  {APBKT.map(b => <th key={b.key} style={{ ...hCell, textAlign: "right" }}>{b.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {shownVendors.map((d, i) => {
                  const ob = parseFloat(d.open_balance) || 0;
                  const pctAP = apVendTot.open ? ob / apVendTot.open : 0;
                  const clickable = !!(apLines && apLines.some(l => l.consolidatedname === d.consolidatedname));
                  return (
                    <tr key={i} style={{ cursor: clickable ? "pointer" : "default" }}
                      onClick={() => { if (clickable) setDetailVendor(d.consolidatedname); }}
                      onMouseEnter={e => e.currentTarget.style.background = "var(--color-background-secondary)"} onMouseLeave={e => e.currentTarget.style.background = ""}>
                      <Td style={{ fontWeight: 500, color: clickable ? B.cyanDark : undefined }}>{d.consolidatedname}</Td>
                      {vendorDesigs.length > 0 && <Td>{d.designation || "\u2014"}</Td>}
                      <TdNum>{fmt(ob)}</TdNum>
                      <TdNum>{(pctAP * 100).toFixed(1)}%</TdNum>
                      {APBKT.map(b => <TdNum key={b.key}>{fmt(d[b.key] || 0)}</TdNum>)}
                    </tr>
                  );
                })}
              </tbody>
            </table>}
        </Card>
      </div>
    )}

    {/* ══════════ SLOW MOVING INVENTORY ══════════
        Inventory sitting on the shelf faster than it sells. Units held above
        what actually sold in the trailing twelve months are advanced against at
        a lower rate; units above trailing TWENTY-FOUR months, lower still. The
        two excess columns are the whole point of the tab. */}
    {tab === "sm" && <SlowMovingTab cur={h} smLines={smLines} skus={partyTrend.skus} editing={editing}
                                      onOpen={id => go("collateral-bbc", { bbid: id })} />}

    </div>
    </div>

    {/* ── Per-debtor invoice detail popup (invoice-grain) — sortable + exportable ── */}
    {detailDebtor != null && (
      <Modal wide title={`${detailDebtor} — Invoices (${detailRows.length})`} onClose={() => setDetailDebtor(null)}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", marginBottom: 10 }}>
          <Btn onClick={exportCsv} disabled={!detailRows.length}>⬇ Export CSV</Btn>
        </div>
        {detailRows.length === 0
          ? <div style={{ padding: 20, textAlign: "center", color: "var(--color-text-secondary)", fontSize: 13 }}>No invoice detail for this debtor.</div>
          : <div style={{ maxHeight: "60vh", overflow: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, whiteSpace: "nowrap" }}>
              <thead><tr>
                {hasDesigAR && <th style={{ ...thSticky, textAlign: "left" }}>Designation</th>}
                {DCOLS.map(c => (
                  <th key={c.k} onClick={() => dtoggle(c.k)} style={{ ...thSticky, textAlign: c.t === "num" ? "right" : "left", cursor: "pointer", userSelect: "none" }}>
                    {c.label}<span style={{ fontSize: 10, opacity: dsort.key === c.k ? 1 : 0.25 }}> {dsort.key === c.k ? (dsort.dir === "asc" ? "▲" : "▼") : "▲"}</span>
                  </th>
                ))}
              </tr></thead>
              <tbody>
                {sortedDetail.map((r, i) => (
                  <tr key={i} onMouseEnter={e => e.currentTarget.style.background = "var(--color-background-secondary)"} onMouseLeave={e => e.currentTarget.style.background = ""}>
                    {hasDesigAR && <Td>{r.designation || "—"}</Td>}
                    <Td><span style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>{r.invoicenumber || "—"}</span></Td>
                    <Td>{fmtDate(r.invoicedate)}</Td>
                    <Td>{fmtDate(r.duedate)}</Td>
                    <TdNum>{fmt(r.openbalance || 0)}</TdNum>
                    <TdNum>{fmt(r.aging_inv_0_30 || 0)}</TdNum>
                    <TdNum>{fmt(r.aging_inv_31_60 || 0)}</TdNum>
                    <TdNum>{fmt(r.aging_inv_61_90 || 0)}</TdNum>
                    <TdNum>{fmt(r.aging_inv_91_120 || 0)}</TdNum>
                    <TdNum>{fmt(r.aging_inv_over120 || 0)}</TdNum>
                    <TdNum>{fmt(r.pastdue || 0)}</TdNum>
                    <TdNum>{fmt(r.eligiblear || 0)}</TdNum>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr>
                {hasDesigAR && <Td style={tfootCell}></Td>}
                <Td style={tfootCell}>Total ({detailRows.length})</Td>
                <Td style={tfootCell}></Td>
                <Td style={tfootCell}></Td>
                <TdNum style={tfootCell}>{fmt(dTot.ob)}</TdNum>
                <TdNum style={tfootCell}>{fmt(dTot.b0)}</TdNum>
                <TdNum style={tfootCell}>{fmt(dTot.b1)}</TdNum>
                <TdNum style={tfootCell}>{fmt(dTot.b2)}</TdNum>
                <TdNum style={tfootCell}>{fmt(dTot.b3)}</TdNum>
                <TdNum style={tfootCell}>{fmt(dTot.b4)}</TdNum>
                <TdNum style={tfootCell}>{fmt(dTot.past)}</TdNum>
                <TdNum style={tfootCell}>{fmt(dTot.elig)}</TdNum>
              </tr></tfoot>
            </table>
          </div>}
      </Modal>
    )}

    {/* ── Per-vendor A/P invoice detail popup (due-date aging) ── */}
    {detailVendor != null && (
      <Modal wide title={`${detailVendor} — A/P Invoices (${apVendorRows.length})`} onClose={() => setDetailVendor(null)}>
        {apVendorRows.length === 0
          ? <div style={{ padding: 20, textAlign: "center", color: "var(--color-text-secondary)", fontSize: 13 }}>No A/P invoice detail for this vendor.</div>
          : <div style={{ maxHeight: "60vh", overflow: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, whiteSpace: "nowrap" }}>
              <thead><tr>
                {hasDesigAP && <th style={{ ...thSticky, textAlign: "left" }}>Designation</th>}
                <th style={{ ...thSticky, textAlign: "left" }}>Invoice #</th>
                <th style={{ ...thSticky, textAlign: "left" }}>Invoice Date</th>
                <th style={{ ...thSticky, textAlign: "left" }}>Due Date</th>
                <th style={{ ...thSticky, textAlign: "right" }}>Open Balance</th>
                {APBKT.map(b => <th key={b.key} style={{ ...thSticky, textAlign: "right" }}>{b.label}</th>)}
                <th style={{ ...thSticky, textAlign: "right" }}>DPD</th>
              </tr></thead>
              <tbody>
                {apVendorRows.map((r, i) => (
                  <tr key={i} onMouseEnter={e => e.currentTarget.style.background = "var(--color-background-secondary)"} onMouseLeave={e => e.currentTarget.style.background = ""}>
                    {hasDesigAP && <Td>{r.designation || "—"}</Td>}
                    <Td><span style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>{r.invoicenumber || "—"}</span></Td>
                    <Td>{fmtDate(r.invoicedate)}</Td>
                    <Td>{fmtDate(r.duedate)}</Td>
                    <TdNum>{fmt(r.openbalance || 0)}</TdNum>
                    {APBKT.map(b => <TdNum key={b.key}>{fmt(r[b.key] || 0)}</TdNum>)}
                    <TdNum>{r.dpd == null ? "—" : r.dpd}</TdNum>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr>
                {hasDesigAP && <Td style={tfootCell}></Td>}
                <Td style={tfootCell}>Total ({apVendorRows.length})</Td>
                <Td style={tfootCell}></Td>
                <Td style={tfootCell}></Td>
                <TdNum style={tfootCell}>{fmt(apVendorTot.ob)}</TdNum>
                {APBKT.map(b => <TdNum key={b.key} style={tfootCell}>{fmt(apVendorTot[b.key])}</TdNum>)}
                <TdNum style={tfootCell}></TdNum>
              </tr></tfoot>
            </table>
          </div>}
      </Modal>
    )}

  </>;
};
